import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailCreditsRow,
    type DetailCredit,
    type DetailCreditColumn,
} from '~/ui-rocco/pages/detail-shared';
import { PagesEnum } from '~/utils/route-config';
import { useRailTileClickReport } from '~/ui-rocco/hooks/tile-click-event-for-section';

/**
 * Where a tag pill goes: the projects directory with the tag as its search
 * term. A stand-in until tags have somewhere of their own — free-text search
 * is the only route that takes an arbitrary tag today, and it does read them
 * (the directory shows the term in its search field and filters on it).
 */
const tagSearchLink = (tag: string) =>
    `${PagesEnum.PROJECTS}?search=${encodeURIComponent(tag)}`;

/** A professional's profile click, as the used-professionals rail reports it. */
type ReportProfessionalClick = (item: {
    id: number;
    title: string;
    professionalId?: number;
}) => void;

/** One credited name, before it is deduplicated into a column. */
interface CreditSource {
    name?: string | null;
    link?: string | null;
    role?: string | null;
    /** The credited professional's ID; zero or absent for a name with no profile. */
    professionalID?: number | null;
}

type CreditColumnsData = Pick<
    ReturnType<typeof useLoaderData<typeof loader>>,
    'project' | 'projectTeamMembers' | 'featuredProducts'
>;

/**
 * A name's tracking: the impression it logs once in view and the click it
 * reports, for a name with a profile behind it. Nothing for one without.
 */
const creditTracking = (
    name: string,
    professionalID: number | null | undefined,
    reportClick: ReportProfessionalClick,
): Pick<DetailCredit, 'impression' | 'onClick'> => {
    if (!professionalID || professionalID <= 0) return {};
    return {
        impression: { itemID: professionalID, professionalID },
        onClick: () =>
            reportClick({
                id: professionalID,
                title: name,
                professionalId: professionalID,
            }),
    };
};

/**
 * Deduplicates credits by name, in order: the first entry for a name wins.
 */
const dedupeCredits = (
    sources: CreditSource[],
    reportClick: ReportProfessionalClick,
): DetailCredit[] => {
    const credits: DetailCredit[] = [];
    const seen = new Set<string>();
    for (const source of sources) {
        const name = (source.name ?? '').trim();
        const key = name.toLowerCase();
        if (!name || seen.has(key)) continue;
        seen.add(key);
        credits.push({
            name,
            link: source.link || undefined,
            role: source.role?.trim() || undefined,
            ...creditTracking(name, source.professionalID, reportClick),
        });
    }
    return credits;
};

/**
 * The credited team from the structured sources we have: the lead
 * professional, any project-group team members, and the project's
 * photographers.
 */
const teamCreditSources = ({
    project,
    projectTeamMembers,
}: CreditColumnsData): CreditSource[] => {
    const { Professional } = project;
    const sources: CreditSource[] = [
        {
            name: Professional.Title,
            link: Professional.Link,
            role: Professional.Categories?.[0]?.PrettyTitle,
            professionalID: Professional.ID,
        },
    ];
    for (const group of projectTeamMembers ?? []) {
        for (const member of group.TeamMembers ?? []) {
            sources.push({
                name: member.ProfessionalName || member.Name,
                link: member.Link,
                role: group.Category,
                professionalID: member.ProfessionalID,
            });
        }
    }
    for (const photographer of project.Photographers ?? []) {
        sources.push({
            name: photographer.Title,
            link: photographer.Link,
            role: 'Photographer',
            professionalID: photographer.ID,
        });
    }
    return sources;
};

/**
 * Every supplier whose product is tagged into this project: a project
 * commonly carries several products from the same maker, and the column
 * names makers, not products.
 */
const supplierCreditSources = ({
    featuredProducts,
}: CreditColumnsData): CreditSource[] =>
    (featuredProducts?.FeaturedProducts?.products ?? []).map((product) => ({
        name: product.professional?.name,
        link: product.professional?.link,
        professionalID: product.professional?.id,
    }));

/**
 * The credit columns: the credited team and the suppliers whose products are
 * tagged into the project. Each name with a profile logs an impression and
 * reports its click as the used-professionals rail does.
 */
export const buildProjectCreditColumns = (
    data: CreditColumnsData,
    reportClick: ReportProfessionalClick,
): DetailCreditColumn[] => {
    const credits = dedupeCredits(teamCreditSources(data), reportClick);
    const suppliers = dedupeCredits(supplierCreditSources(data), reportClick);
    return [
        { label: 'Project team', entries: credits },
        {
            label: 'Product Suppliers',
            entries: suppliers,
            // Two columns once the list is long enough to need them;
            // a project with a handful of suppliers reads better as one.
            columns: suppliers.length > 5 ? 2 : 1,
        },
    ];
};

/**
 * The credit columns for this page. Read by the row under the summary and,
 * on the phone, by the description that carries them behind Read more.
 */
export const useProjectCreditColumns = (): DetailCreditColumn[] => {
    const data = useLoaderData<typeof loader>();
    const reportClick = useRailTileClickReport(
        'ProjectUsedProfessionals',
        'ProfessionalTile',
    );
    return buildProjectCreditColumns(data, reportClick);
};

/**
 * P4 — meta row under the summary: the search tags, the credited team and
 * the suppliers whose products are tagged into the project, drawn by the
 * shared row the product and article pages use too.
 */
export const ProjectCreditsRow = ({
    hideMobileTagsLabel = false,
    showColumns = true,
}: {
    /** Set the tag pills without their label below md. */
    hideMobileTagsLabel?: boolean;
    /** Without the team and supplier columns: the tags alone. */
    showColumns?: boolean;
} = {}) => {
    const { project } = useLoaderData<typeof loader>();
    const leadName = project.Professional.Title ?? '';
    const tags = (project.SearchTags ?? [])
        .map((tag) => tag?.Text)
        .filter((text): text is string => !!text && text !== leadName)
        .map((label) => ({ label, to: tagSearchLink(label) }));
    const columns = useProjectCreditColumns();

    return (
        <DetailCreditsRow
            theme="dark"
            tags={tags}
            hideMobileTagsLabel={hideMobileTagsLabel}
            columns={showColumns ? columns : []}
        />
    );
};
